import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { rawFetch } from "@/lib/api/client";
import { ErrorBox } from "@/components/common";

type Proposal = {
  id: string; kind: string; normalized_claim: { name?: string };
  source_locator: string; evidence_withheld: boolean;
};
type Queue = { count: number; results: Proposal[] };
type Addition = { proposal_id: string; kind: string; name: string;
  class?: string; definition: string };
type Conflict = { proposal_id: string; code: string };
type Preview = { base_version_id: string | null; preview_hash: string;
  additions: Addition[]; conflicts: Conflict[] };
type Confirmed = { selection_id: string; draft_version_id: string;
  status: "draft"; published: false };

export function DiscoveryDraftPanel({ workspaceId, canEdit, canAdmin }: {
  workspaceId: string; canEdit: boolean; canAdmin: boolean;
}) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string[]>([]);
  const [offset, setOffset] = useState(0);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [confirmed, setConfirmed] = useState<Confirmed | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const queue = useQuery<Queue>({
    queryKey: ["discovery-approved-for-draft", workspaceId, offset],
    queryFn: () => rawFetch<Queue>(`/catalog/discovery/proposals/?workspace=${encodeURIComponent(workspaceId)}&selectable=true&limit=100&offset=${offset}`),
    enabled: Boolean(workspaceId && canEdit),
  });
  useEffect(() => {
    if (queue.data && offset > 0 && offset >= queue.data.count) {
      setOffset(Math.max(0, Math.floor((queue.data.count - 1) / 100) * 100));
    }
  }, [offset, queue.data]);

  if (!canEdit) return null;

  function toggle(id: string) {
    setSelected((old) => old.includes(id) ? old.filter((item) => item !== id) : [...old, id]);
    setPreview(null);
    setConfirmed(null);
    setRequestId(null);
    setError(null);
  }

  async function makePreview() {
    setBusy(true);
    setError(null);
    setConfirmed(null);
    try {
      const result = await rawFetch<Preview>("/catalog/discovery/drafts/preview/", {
        method: "POST", body: JSON.stringify({ workspace: workspaceId, proposal_ids: selected }),
      });
      setPreview(result);
      setRequestId(crypto.randomUUID());
    } catch (caught) {
      setPreview(null);
      setError(caught as Error);
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (!preview || !requestId || preview.conflicts.length || !preview.additions.length) return;
    setBusy(true);
    setError(null);
    try {
      const result = await rawFetch<Confirmed>("/catalog/discovery/drafts/confirm/", {
        method: "POST", body: JSON.stringify({
          workspace: workspaceId, proposal_ids: selected,
          base_version_id: preview.base_version_id,
          preview_hash: preview.preview_hash, request_id: requestId,
        }),
      });
      setConfirmed(result);
      qc.invalidateQueries({ queryKey: ["discovery-approved-for-draft", workspaceId] });
      qc.invalidateQueries({ queryKey: ["discovery-review", workspaceId] });
    } catch (caught) {
      setError(caught as Error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-label="Build ontology draft" className="mt-6 space-y-3 rounded border border-border bg-bg p-4">
      <div>
        <h3 className="text-sm font-semibold text-fg">Build an ontology draft</h3>
        <p className="mt-1 text-xs text-muted">Select approved claims, preview additive changes and conflicts, then ask a workspace admin to confirm. This creates one draft; it does not publish.</p>
      </div>
      {queue.isLoading && <p role="status" className="text-xs text-muted">Loading approved claims…</p>}
      {queue.isError && <ErrorBox error={queue.error} />}
      {queue.data?.count === 0 && <p className="text-xs text-muted">No approved claims are ready for a draft.</p>}
      {queue.data && queue.data.results.length > 0 && <div className="space-y-2">
        {queue.data.results.map((item) => <label key={item.id}
          className="flex cursor-pointer items-start gap-3 rounded border border-border p-3 text-xs text-fg focus-within:ring-2 focus-within:ring-accent">
          <input type="checkbox" checked={selected.includes(item.id)} onChange={() => toggle(item.id)}
            disabled={!selected.includes(item.id) && selected.length >= 100}
            aria-label={`Select ${item.normalized_claim.name ?? item.kind}`} />
          <span><strong>{item.normalized_claim.name}</strong> · {item.kind.replace("_", " ")}
            <span className="block text-muted">{item.source_locator}{item.evidence_withheld ? " · evidence withheld" : ""}</span>
          </span>
        </label>)}
        {queue.data.count > 100 && <div className="flex items-center justify-between gap-2 text-xs text-muted">
          <span>Showing {offset + 1}–{offset + queue.data.results.length} of {queue.data.count}. Select up to 100 claims per draft.</span>
          <div className="flex gap-2">
            <button type="button" className="rounded border border-border px-2 py-1 disabled:opacity-40"
              disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 100))}>Previous</button>
            <button type="button" className="rounded border border-border px-2 py-1 disabled:opacity-40"
              disabled={offset + 100 >= queue.data.count} onClick={() => setOffset(offset + 100)}>Next</button>
          </div>
        </div>}
      </div>}
      <button type="button" className="btn-primary" disabled={!selected.length || busy}
        onClick={makePreview}>Preview selected claims</button>
      {error && <ErrorBox error={error} />}
      {preview && <div aria-label="Draft preview" className="space-y-3 rounded border border-border bg-border/10 p-4 text-xs">
        <p className="text-muted">Base: {preview.base_version_id ? preview.base_version_id.slice(0, 8) : "new ontology"}</p>
        <ul className="list-inside list-disc space-y-1 text-fg">
          {preview.additions.map((item) => <li key={item.proposal_id}>
            {item.kind === "property" ? `${item.class}.${item.name}` : item.name} · {item.definition}
          </li>)}
        </ul>
        {preview.conflicts.length > 0 && <div role="alert" className="text-bad">
          <strong>Resolve conflicts before confirming:</strong>
          <ul className="list-inside list-disc">{preview.conflicts.map((item) =>
            <li key={item.proposal_id}>{item.proposal_id.slice(0, 8)} · {item.code.replaceAll("_", " ")}</li>)}</ul>
        </div>}
        {canAdmin && <button type="button" className="btn-primary"
          disabled={busy || Boolean(confirmed) || preview.conflicts.length > 0 || preview.additions.length === 0}
          onClick={confirm}>{confirmed ? "Draft created" : "Confirm and create draft"}</button>}
        {!canAdmin && <p className="text-muted">A workspace admin or owner must confirm this preview.</p>}
      </div>}
      {confirmed && <p role="status" className="text-xs text-good">
        Draft {confirmed.draft_version_id.slice(0, 8)} created from this selection. Publication still requires the ontology review gates.
      </p>}
    </section>
  );
}
